/**
 * Page Amis : ajout par le nom, amis avec leur présence (rejoindre, inviter, retirer,
 * bloquer), demandes reçues et envoyées, joueurs croisés récemment, joueurs bloqués.
 */
import { useEffect, useState } from 'preact/hooks';
import { Ban, History, Inbox, UserPlus, Users } from 'lucide-preact';
import type { RecentPlayer } from '@snowchain/shared';
import { lobby } from './lobby';
import { menu, type FriendsTab } from './state';
import { act, requestFriend } from './actions';
import { AddFriendForm, FriendRow, RequestRow } from './social';
import { Avatar, Btn, Empty, Segmented, Spinner, relTime } from './ui';

function RecentList() {
  const [players, setPlayers] = useState<RecentPlayer[] | null>(null);
  const social = lobby.social.value;
  useEffect(() => {
    let alive = true;
    lobby
      .req({ op: 'recent' })
      .then((r) => alive && setPlayers(r.players))
      .catch(() => alive && setPlayers([]));
    return () => {
      alive = false;
    };
    // Recharge quand la liste d'amis change (un joueur récent devenu ami disparaît).
  }, [social.friends.length, social.outgoing.length, social.blocked.length]);
  if (players === null)
    return (
      <div class="mn-table-msg">
        <Spinner /> Chargement...
      </div>
    );
  if (!players.length)
    return (
      <Empty icon={History} title="Personne pour l’instant">
        Les joueurs qui ont visité vos parties, ou ceux que vous avez croisés chez d’autres, apparaîtront ici.
      </Empty>
    );
  const pending = new Set(social.outgoing.map((r) => r.id));
  return (
    <div class="mn-list">
      {players.map((p) => (
        <div class="mn-friend" key={p.id}>
          <Avatar id={p.id} name={p.name} size={36} />
          <div class="mn-friend-text">
            <span class="mn-friend-name">{p.name}</span>
            <span class="mn-friend-presence">
              {p.worldName} · {relTime(p.at)}
            </span>
          </div>
          <span class="mn-friend-actions">
            <Btn size="sm" icon={UserPlus} disabled={pending.has(p.id)} onClick={() => void requestFriend(p.name)}>
              {pending.has(p.id) ? 'Demande envoyée' : 'Ajouter'}
            </Btn>
          </span>
        </div>
      ))}
    </div>
  );
}

export function FriendsView() {
  const s = lobby.social.value;
  const tab = menu.friendsTab.value;
  const online = s.friends.filter((f) => f.presence.state !== 'offline').length;
  const options: { value: FriendsTab; label: string }[] = [
    { value: 'friends', label: `Amis · ${s.friends.length}` },
    { value: 'requests', label: `Demandes · ${s.incoming.length + s.outgoing.length}` },
    { value: 'recent', label: 'Joueurs récents' },
    { value: 'blocked', label: `Bloqués · ${s.blocked.length}` },
  ];
  return (
    <div class="mn-friends">
      <header class="mn-page-head">
        <div>
          <h1>Amis</h1>
          <p class="mn-muted">
            {s.friends.length} ami{s.friends.length > 1 ? 's' : ''} · {online} en ligne{s.incoming.length ? ` · ${s.incoming.length} demande${s.incoming.length > 1 ? 's' : ''} à traiter` : ''}
          </p>
        </div>
      </header>
      <section class="mn-card mn-add-card">
        <div>
          <h3>Ajouter un ami</h3>
          <p class="mn-muted mn-small">Saisissez son nom exactement comme il apparaît en jeu (majuscules et accents ignorés).</p>
        </div>
        <AddFriendForm />
      </section>
      <Segmented label="Section" value={tab} onChange={(v) => (menu.friendsTab.value = v)} options={options} />
      <section class="mn-card mn-list-card">
        {tab === 'friends' &&
          (s.friends.length ? (
            <div class="mn-list">
              {s.friends.map((f) => (
                <FriendRow key={f.id} f={f} mode="page" />
              ))}
            </div>
          ) : (
            <Empty icon={Users} title="Votre liste d’amis est vide">
              Ajoutez un joueur ci-dessus, ou depuis l’onglet « Joueurs récents ».
            </Empty>
          ))}
        {tab === 'requests' &&
          (s.incoming.length || s.outgoing.length ? (
            <div class="mn-list">
              {s.incoming.length > 0 && <p class="mn-side-label">Reçues · {s.incoming.length}</p>}
              {s.incoming.map((r) => (
                <RequestRow key={r.id} r={r} dir="in" />
              ))}
              {s.outgoing.length > 0 && <p class="mn-side-label">Envoyées · {s.outgoing.length}</p>}
              {s.outgoing.map((r) => (
                <RequestRow key={r.id} r={r} dir="out" />
              ))}
            </div>
          ) : (
            <Empty icon={Inbox} title="Aucune demande en attente" />
          ))}
        {tab === 'recent' && <RecentList />}
        {tab === 'blocked' &&
          (s.blocked.length ? (
            <div class="mn-list">
              {s.blocked.map((b) => (
                <div class="mn-friend" key={b.id}>
                  <Avatar id={b.id} name={b.name} size={36} />
                  <div class="mn-friend-text">
                    <span class="mn-friend-name">{b.name}</span>
                    <span class="mn-friend-presence">Ne peut ni vous demander en ami ni vous inviter</span>
                  </div>
                  <span class="mn-friend-actions">
                    <Btn size="sm" onClick={() => void act({ op: 'unblock', id: b.id }, 'Joueur débloqué', b.name)}>
                      Débloquer
                    </Btn>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <Empty icon={Ban} title="Personne n’est bloqué" />
          ))}
      </section>
    </div>
  );
}
